// internationalization
import type { TFunction } from 'i18next';

// libraries
import { z } from 'zod';

export function createAddNodeFormSchema(t: TFunction) {
  return z
    .object({
      name: z
        .string()
        .min(1, t('input.required', { ns: 'validation' }))
        .max(100, t('input.tooLong', { ns: 'validation', max: 100 })),
      type: z.string().min(1, t('input.required', { ns: 'validation' })),
      sourceType: z.enum(['dataStreams', 'virtual', 'residual']),
      dataStreams: z.array(
        z.object({
          unitId: z.number(),
          unitTypeInputId: z.number().nullable().optional(),
          dataType: z.string(),
          aggregateType: z.string(),
        }),
      ),
      options: z.object({
        specialNodeType: z.string().nullable(),
        consumption: z.number().nullable(),
        percentage: z.number().min(0).max(1).nullable(),
        openingHours: z.record(z.string(), z.unknown()).optional(),
        goShares: z.array(z.unknown()).optional(),
      }),
      locationId: z.number().nullable(),
    })
    .refine(
      (data) => {
        // When sourceType is 'dataStreams', at least one dataStream is required
        if (data.sourceType === 'dataStreams') {
          return data.dataStreams.length > 0;
        }
        return true;
      },
      {
        message: t('input.required', { ns: 'validation' }),
        path: ['dataStreams'],
      },
    )
    .refine(
      (data) => {
        // When sourceType is 'residual', specialNodeType must be set
        if (data.sourceType === 'residual') {
          return (
            data.options.specialNodeType !== null &&
            data.options.specialNodeType !== ''
          );
        }
        return true;
      },
      {
        message: t('input.required', { ns: 'validation' }),
        path: ['options', 'specialNodeType'],
      },
    )
    .refine(
      (data) => {
        // When specialNodeType is 'FlatResidual', consumption must be set
        if (data.options.specialNodeType === 'FlatResidual') {
          return (
            data.options.consumption !== null && data.options.consumption >= 0
          );
        }
        return true;
      },
      {
        message: t('input.required', { ns: 'validation' }),
        path: ['options', 'consumption'],
      },
    )
    .refine(
      (data) => {
        // When specialNodeType is 'PercentageResidual', percentage must be set
        if (data.options.specialNodeType === 'PercentageResidual') {
          return (
            data.options.percentage !== null && data.options.percentage > 0
          );
        }
        return true;
      },
      {
        message: t('input.required', { ns: 'validation' }),
        path: ['options', 'percentage'],
      },
    );
}

export type AddNodeFormValues = z.infer<
  ReturnType<typeof createAddNodeFormSchema>
>;
